import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Colors } from 'planoplan-ui-kit/core/colors';
import { useTranslations } from '@libs/translations';
import { SectionNumber } from '@components/Section-Number';
import { Separator } from '@components/Separator';
import { SectionAnchor } from '@components/Section-Anchor';
import { Picture } from '@components/Picture';
import { Points } from '@styles';

const CONTENT_SCROLL_DELAY = 380;

export const VisualizationConcept = () => {
  const translations = useTranslations();
  const sectionRef = useRef(null);
  const stickyContentRef = useRef(null);
  const contentRef = useRef(null);

  const pictureData = {
    '670': [`/images/visualization-concept-mob-1x.jpg`, `/images/visualization-concept-mob-1x.webp`],
    '1x': [`/images/visualization-concept-mob-1x.jpg`, `/images/visualization-concept-mob-1x.webp`],
    '2x': [`/images/visualization-concept-mob-2x.jpg`, `/images/visualization-concept-mob-2x.webp`],
  };

  useEffect(() => {
    const section = sectionRef.current;
    const stickyContent = stickyContentRef.current;
    const content = contentRef.current;

    if (!section || !stickyContent || !content) return;

    const updateContentPosition = () => {
      const scrollDistance = section.offsetHeight - stickyContent.offsetHeight;
      const contentScrollDistance = Math.max(scrollDistance - CONTENT_SCROLL_DELAY, 0);
      const progress =
        contentScrollDistance > 0
          ? Math.min(Math.max(-section.getBoundingClientRect().top / contentScrollDistance, 0), 1)
          : 0;
      const translateY = 100 - progress * 100;

      content.style.setProperty('--content-translate-y', `${translateY}%`);
    };

    updateContentPosition();

    window.addEventListener('scroll', updateContentPosition, { passive: true });
    window.addEventListener('resize', updateContentPosition);

    return () => {
      window.removeEventListener('scroll', updateContentPosition);
      window.removeEventListener('resize', updateContentPosition);
    };
  }, []);

  return (
    <View id="visualization-concept" ref={sectionRef}>
      <StickyContent ref={stickyContentRef}>
        <LeftScrollArea>
          <Content ref={contentRef}>
            <WrapperTop>
              <SectionNumber number={''} text={translations.t('site.home.visualization-concept.name')} />
              <Title>{translations.t('site.home.visualization-concept.title')}</Title>
              <Description>{translations.t('site.home.visualization-concept.description')}</Description>
            </WrapperTop>
            <PictureWrapper>
              <Picture
                data={pictureData}
                alt={translations.t('site.home.visualization-concept.alt')}
                lazy={true}
                width="100%"
                height="100%"
              />
            </PictureWrapper>
            <WrapperBottom>
              <Separator />
              <Text dangerouslySetInnerHTML={{ __html: translations.t('site.home.visualization-concept.text') }}></Text>
              <SectionAnchor text={translations.t('site.home.visualization-concept.anchorAi')} />
            </WrapperBottom>
          </Content>
        </LeftScrollArea>
        <RightScrollArea />
      </StickyContent>
    </View>
  );
};

const View = styled.section`
  display: flex;
  align-items: center;
  justify-content: center;

  @media ${Points.tablet_landscape} {
    height: auto;
    justify-content: left;
    background-image: image-set(
      url('/images/visualization-concept-bg.webp') type('image/webp'),
      url('/images/visualization-concept-bg.jpg') type('image/jpeg')
    );
    background-size: cover;
    background-position: center;
  }

  @media ${Points.laptop_wide} {
    display: block;
    height: calc(200vh + ${CONTENT_SCROLL_DELAY}px);
    min-height: 1890px;
    background-image: none;
  }

  @media ${Points.fullhd} {
    min-height: 2140px;
  }
`;

const StickyContent = styled.div`
  width: 100%;

  @media ${Points.laptop_wide} {
    position: sticky;
    top: 0;
    display: flex;
    width: 100%;
    height: 100vh;
    min-height: 895px;
    overflow: hidden;
    background-image: image-set(
      url('/images/visualization-concept-fullbg.webp') type('image/webp'),
      url('/images/visualization-concept-fullbg.jpg') type('image/jpeg')
    );
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
  }

  @media ${Points.fullhd} {
    min-height: 1020px;
  }
`;

const LeftScrollArea = styled.div`
  width: 100%;
  overflow: hidden;

  @media ${Points.laptop_wide} {
    width: 658px;
    height: 100%;
    position: relative;
  }

  @media (min-width: 1600px) {
    width: calc(50vw - 122px);
  }

  @media ${Points.fullhd} {
    width: calc(50vw - 202px);
  }
`;

const RightScrollArea = styled.div`
  display: none;

  @media ${Points.laptop_wide} {
    display: block;
    flex: 1;
    height: 100%;
  }
`;

const Content = styled.div`
  width: 100%;
  background-color: ${Colors.main_beige};

  @media ${Points.tablet_landscape} {
    width: calc(50vw - 512px + 28px + 28px + 430px);
    min-width: 486px;
    padding: 40px 28px 61px;
  }

  @media ${Points.laptop_wide} {
    width: inherit;
    min-width: 658px;
    height: 100vh;
    min-height: 895px;
    padding: 80px 0 60px;
    transform: translateY(var(--content-translate-y, 100%));
    will-change: transform;
  }

  @media ${Points.fullhd} {
    padding-top: 120px;
    min-height: 1020px;
  }
`;

const WrapperTop = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  padding: 30px 16px 15px;

  @media ${Points.tablet_landscape} {
    align-items: start;
    width: 430px;
    margin-left: auto;
    padding: 0;
  }

  @media ${Points.laptop_wide} {
    width: 678px;
    margin-left: auto;
    padding: 0 80px;
  }

  @media ${Points.fullhd} {
    width: 600px;
    padding: 0;
    padding-right: 80px;
  }
`;

const WrapperBottom = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  padding: 0 16px 30px;

  @media ${Points.tablet_landscape} {
    width: 430px;
    margin-left: auto;
    padding: 0;
  }

  @media ${Points.laptop_wide} {
    width: 678px;
    margin-left: auto;
    padding: 0 80px;
  }

  @media ${Points.fullhd} {
    width: 600px;
    padding: 0;
    padding-right: 80px;
  }
`;

const Title = styled.h2`
  margin-top: -5px;
  margin-bottom: 15px;
  font-weight: 400;
  font-size: 26px;
  line-height: 1;
  letter-spacing: 0%;
  font-variant-numeric-fraction: stacked-fractions;

  @media ${Points.tablet_landscape} {
    font-size: 34px;
  }

  @media ${Points.laptop_wide} {
    font-size: 54px;
  }
`;

const Description = styled.p`
  font-weight: 400;
  font-size: 12px;
  line-height: 1.1;
  letter-spacing: 1%;

  @media ${Points.laptop_wide} {
    font-size: 14px;
  }
`;

const PictureWrapper = styled.div`
  width: 100%;

  @media ${Points.tablet_landscape} {
    display: none;
  }
`;

const Text = styled.div`
  font-weight: 400;
  font-size: 16px;
  line-height: 1.3;
  letter-spacing: 0%;
  font-variant-numeric-fraction: stacked-fractions;

  p:not(:last-child) {
    margin-bottom: 15px;
  }

  p:last-child {
    margin-bottom: 30px;
  }

  @media ${Points.laptop_wide} {
    font-size: 18px;
  }
`;
